// studentapps/Login.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Email as EmailIcon,
  Security as SecurityIcon,
  School as SchoolIcon,
  CorporateFare as CorporateIcon
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import FloatingHomeReturn from '../components/FloatingHomeReturn';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithEmail, signInWithOTP } = useAuth();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setMessage('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email) {
      setMessage('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setMessage('');
    
    const result = tabValue === 0 
      ? await signInWithEmail(email)
      : await signInWithOTP(email);
    
    if (result.success) {
      setMessage(result.message);
    } else {
      setMessage(`Error: ${result.error}`);
    }
    setIsLoading(false);
  };

  return (
    <Container component="main" maxWidth="sm">
      <FloatingHomeReturn/>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
          py: 4,
          position: 'relative'
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: 'rgba(255, 255, 255, 0.1)',
          padding: '8px 16px',
          borderRadius: 3,
          backdropFilter: 'blur(10px)'
        }}>
          <CorporateIcon sx={{ fontSize: 20, color: '#ff4d4d' }} />
          <Typography variant="body2" sx={{ 
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.9rem' }
          }}>
            Zolang Nexora
          </Typography>
        </Box>

        <Paper
          elevation={24}
          sx={{
            padding: { xs: 4, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 480,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            mx: 2
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mb: 3 
          }}>
            <SchoolIcon sx={{ fontSize: { xs: 40, sm: 48 }, color: 'primary.main' }} />
            <Box>
              <Typography component="h1" variant="h3" fontWeight="700" color="primary" sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                Student Suite
              </Typography>
              <Typography variant="h6" color="secondary" sx={{ mt: -0.5, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                Modern Learning Platform
              </Typography>
            </Box>
          </Box>

          <Card sx={{ width: '100%', mt: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                centered
                sx={{
                  '& .MuiTab-root': { py: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } },
                  '& .Mui-selected': { color: 'primary.main' }
                }}
              >
                <Tab 
                  icon={<EmailIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />} 
                  label="Magic Link" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<SecurityIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />} 
                  label="One-Time Code" 
                  iconPosition="start"
                />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Box component="form" onSubmit={handleLogin}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Enter your email to receive a secure magic login link
                  </Typography>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="email"
                    placeholder="student@university.edu"
                    sx={{ mb: 3 }}
                    size="small"
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{ py: 1.5 }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Send Magic Link'}
                  </Button>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box component="form" onSubmit={handleLogin}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Enter your email to receive a one-time verification code
                  </Typography>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="email"
                    placeholder="student@university.edu"
                    sx={{ mb: 3 }}
                    size="small"
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{ py: 1.5 }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Send Verification Code'}
                  </Button>
                </Box>
              </TabPanel>

              {message && (
                <Alert 
                  severity={message.includes('Error') ? 'error' : 'success'}
                  sx={{ m: 3, mt: 0, borderRadius: 2 }}
                >
                  {message}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Secure • Passwordless • Modern
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <CorporateIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                Powered by Zolang Nexora
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;